import { useCallback, useEffect, useRef, useState } from "react";
import { listSisterBrowserQueue } from "../../api";
import type { SisterQueueFilter, SisterQueueResponse } from "../../api/sister-browser";
import { queueErrorMessage } from "./sister-queue-errors";

export const QUEUE_PAGE_SIZE = 50;
interface QueueView { filter: SisterQueueFilter; offset: number }

export function useSisterQueue(refreshKey: number) {
  const [view, setView] = useState<QueueView>({ filter: "active", offset: 0 });
  const [loaded, setLoaded] = useState<{ view: QueueView; result: SisterQueueResponse } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestView = useRef(view);
  const requestVersion = useRef(0);
  const mounted = useRef(true);
  latestView.current = view;

  const load = useCallback(() => {
    const requestedView = latestView.current;
    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    void listSisterBrowserQueue({ limit: QUEUE_PAGE_SIZE, ...requestedView })
      .then((result) => {
        if (!mounted.current || version !== requestVersion.current) return;
        if (requestedView.offset > 0 && requestedView.offset >= result.total) {
          const offset = Math.max(0, Math.ceil(result.total / QUEUE_PAGE_SIZE) - 1) * QUEUE_PAGE_SIZE;
          selectView({ ...requestedView, offset });
          return;
        }
        setLoaded({ view: requestedView, result });
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (!mounted.current || version !== requestVersion.current) return;
        setError(queueErrorMessage(error, "Errore caricamento coda"));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestVersion.current++; };
  }, []);

  useEffect(load, [load, view.filter, view.offset, refreshKey]);

  useEffect(() => {
    const updateVisible = () => { if (document.visibilityState === "visible") load(); };
    const timer = setInterval(updateVisible, 12_000);
    document.addEventListener("visibilitychange", updateVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", updateVisible);
    };
  }, [load]);

  function selectView(next: QueueView) {
    if (next.filter === latestView.current.filter && next.offset === latestView.current.offset) return;
    requestVersion.current++;
    latestView.current = next;
    setView(next);
    setError(null);
    setLoading(true);
  }

  const data = loaded?.view.filter === view.filter && loaded.view.offset === view.offset
    ? loaded.result : null;
  return {
    data, loading, error, view, load,
    setFilter: (filter: SisterQueueFilter) => selectView({ filter, offset: 0 }),
    setOffset: (offset: number) => selectView({ ...latestView.current, offset }),
  };
}
